/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import {
  DRAG_ENDED,
  DRAG_OVER_STOPPED,
  DRAG_OVER_TARGET,
  DRAG_STARTED,
} from "constants/ActionTypes";
import isEqual from "lodash.isequal";
import { createSelector } from "reselect";
import { DragAndDropAction } from "types/Actions";

export enum InsertDirection {
  BEFORE = "BEFORE",
  AFTER = "AFTER",
  INSIDE = "INSIDE",
}

// TODO: Extract enum for type "BLOCK"/"TOKEN"
export type InsertDestination =
  | { type: "BLOCK"; blockId: string }
  | { type: "TOKEN"; blockId: string; tokenIndex: number };

const equalInsertDestination = (
  lhs: InsertDestination,
  rhs: InsertDestination
): boolean => {
  return isEqual(lhs, rhs);
};

export interface RelativeInsertDestination {
  target: InsertDestination;
  insertDirection: InsertDirection;
}

export interface DragAndDropState {
  dragActive: boolean;
  activeInsertDestinations: RelativeInsertDestination[];
}

export const getCurrentInsertDestination = createSelector(
  (state: DragAndDropState) => state?.activeInsertDestinations,
  (activeInsertDestinations): RelativeInsertDestination | undefined =>
    activeInsertDestinations?.[0]
);

export default function DragAndDropReducer(
  dragAndDropState: DragAndDropState,
  action: DragAndDropAction
): DragAndDropState {
  switch (action.type) {
    case DRAG_OVER_TARGET:
      return {
        ...dragAndDropState,
        activeInsertDestinations: (
          dragAndDropState?.activeInsertDestinations ?? []
        )
          .filter(
            (destination) =>
              !equalInsertDestination(destination.target, action.target)
          )
          .concat({
            target: action.target,
            insertDirection: action.insertDirection,
          }),
      };
    case DRAG_OVER_STOPPED:
      return {
        ...dragAndDropState,
        activeInsertDestinations: (
          dragAndDropState?.activeInsertDestinations ?? []
        ).filter(
          (destination) =>
            !equalInsertDestination(destination.target, action.target)
        ),
      };
    case DRAG_ENDED:
      return {
        dragActive: false,
        activeInsertDestinations: [],
      };
    case DRAG_STARTED:
      return {
        ...dragAndDropState,
        dragActive: true,
      };
    default:
      return dragAndDropState;
  }
}
